import {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  Select as MantineSelect,
  SelectProps as MantineSelectProps,
} from '@mantine/core';
import { useIsomorphicEffect } from '@mantine/hooks';
import { IconChevronDown, IconX } from '@tabler/icons-react';
import clsx from 'clsx';
import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ReadOnlyLock } from '../ReadOnlyLock';
import styles from './Select.module.css';

export type SelectSize = 'xs' | 'sm' | 'md' | 'lg';

/** Select option with optional secondary descriptor line shown below the label */
export type SelectItem = ComboboxItem & { description?: string };

export interface SelectProps extends Omit<
  MantineSelectProps,
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'size'
  | 'nothingFoundMessage'
  | 'readOnly'
> {
  /** Input size: 'xs' (24px), 'sm' (32px), 'md' (40px), or 'lg' (48px). Default: 'md' */
  size?: SelectSize;
  /** Add 'fs-mask' class for FullStory masking of sensitive data */
  isMasked?: boolean;
  /** Strip this prefix from option labels in the dropdown and capitalize the remainder */
  optionPrefix?: string;
  /** Remove border and background — use for inline trigger-style selects */
  isTransparent?: boolean;
  /** Right-align the selected value — use when value should sit next to a right-section icon */
  shouldRightAlignValue?: boolean;
  isReadOnly?: boolean;
  /** Readonly visual style. 'label' (default) hides border/section and renders as plain text; 'lock' keeps border and shows a lock icon in place of the chevron. Only applies when `isReadOnly` is true. */
  readOnlyVariant?: 'label' | 'lock';
  /**
   * `'borderless'` — inline trigger style for sorting, filters, toolbars and
   * table headers. Renders as `[label] · [leftSection icon] · [value] · chevron`
   * with no box; the `label` shows inline as a secondary-color prefix and the
   * pill highlights on hover (open shows the selected background). Sizes map
   * xs → 24px, sm/md → 32px, lg → 40px.
   */
  variant?: 'borderless';
}

const isDefined = (value: ReactNode): boolean =>
  value !== undefined && value !== null;

/** Whether the borderless variant should render an inline label/icon group. */
const computeHasInlineLeft = (
  isBorderless: boolean,
  label: ReactNode,
  icon: ReactNode
): boolean => isBorderless && (isDefined(label) || isDefined(icon));

/**
 * Inline label + leading icon group for the borderless variant. Overlays the
 * left of the input (pointer-events:none) so clicks fall through to the trigger.
 */
const BorderlessLeftSection = forwardRef<
  HTMLSpanElement,
  { label?: ReactNode; icon?: ReactNode }
>(({ label, icon }, ref) => (
  <span ref={ref} className={styles.borderlessLeft}>
    {isDefined(label) && (
      <span className={styles.borderlessLabel}>{label}</span>
    )}
    {icon}
  </span>
));
BorderlessLeftSection.displayName = 'BorderlessLeftSection';

/**
 * Track the rendered width of an element (via ResizeObserver) while `enabled`.
 * Used by the borderless variant to reserve input padding equal to the inline
 * label/icon width. Returns 0 when disabled or before the first measurement.
 */
function useMeasuredWidth(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean
): number {
  const [width, setWidth] = useState(0);
  useIsomorphicEffect(() => {
    const el = ref.current;
    if (!enabled || !el) {
      setWidth(0);
      return undefined;
    }
    const measure = () => setWidth(el.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled]);
  return width;
}

/** Duration of each half (out / in) of the borderless value fade. Must match
 *  the opacity transition in Select.module.css. */
const FADE_DURATION_MS = 120;

/** Flatten Mantine `data` (strings, items, or groups) into display labels.
 *  Used to size the borderless trigger to its widest option. */
const labelOf = (item: unknown): string | undefined => {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object' && 'label' in item) {
    return String((item as { label: unknown }).label);
  }
  return undefined;
};

const extractOptionLabels = (data: MantineSelectProps['data']): string[] => {
  if (!data) return [];
  return data
    .flatMap((item) =>
      item && typeof item === 'object' && 'items' in item ? item.items : [item]
    )
    .map(labelOf)
    .filter((label): label is string => label !== undefined);
};

/** Off-screen sizer whose intrinsic width equals the widest option label — the
 *  borderless trigger is frozen to this width so the chevron never slides. */
const ValueSizer = forwardRef<
  HTMLSpanElement,
  { labels: string[]; size: SelectSize }
>(({ labels, size }, ref) => (
  <span
    ref={ref}
    aria-hidden
    className={styles.borderlessSizer}
    data-input-size={size}
  >
    {Array.from(new Set(labels)).map((label) => (
      <span key={label}>{label}</span>
    ))}
  </span>
));
ValueSizer.displayName = 'ValueSizer';

/** Build the error node — suppressed entirely in read-only mode. */
const renderErrorMessage = (
  error: SelectProps['error'],
  isReadOnly: boolean | undefined
): ReactNode => {
  // Suppress error display in read-only mode
  if (isReadOnly) return undefined;
  return error || undefined;
};

/** Default option renderer: optional prefix-strip + capitalize, plus two-line
 *  (label + description) layout when a description is present. */
const createRenderOption =
  (optionPrefix?: string) =>
  ({ option }: ComboboxLikeRenderOptionInput<ComboboxItem>): ReactNode => {
    const description = (option as SelectItem).description;
    let label: ReactNode = option.label;
    if (optionPrefix) {
      const stripped = option.label.replace(optionPrefix, '').trim();
      label = stripped
        ? stripped.charAt(0).toUpperCase() + stripped.slice(1)
        : option.label;
    }
    if (!description) return label;
    return (
      <div className={styles.optionContent}>
        <span className={styles.optionLabel}>{label}</span>
        <span className={styles.optionDescription}>{description}</span>
      </div>
    );
  };

/**
 * Custom rightSection — clear button, lock, or chevron. Rendered ourselves to
 * avoid Mantine's combined-clear-section div (hard-to-override inline styles)
 * and to keep the two icons pixel-aligned. The chevron is wrapped in a .chevron
 * span so CSS rotation targets only it, not consumer-provided rightSection icons.
 */
const renderRightSection = (
  showClearButton: boolean,
  isLockVariant: boolean,
  onClear: (event: React.MouseEvent) => void
): ReactNode => {
  if (showClearButton) {
    return (
      <button
        type="button"
        aria-label="Clear value"
        className={styles.clearButton}
        onClick={onClear}
        tabIndex={-1}
      >
        <IconX size={16} stroke={2} />
      </button>
    );
  }
  if (isLockVariant) {
    return <ReadOnlyLock />;
  }
  // Default 16px chevron; the borderless md/lg variants scale it to 20px
  // via CSS (.chevron svg) to keep this render branch simple.
  return (
    <span className={styles.chevron}>
      <IconChevronDown size={16} stroke={2} />
    </span>
  );
};

/** Extra px added to the measured value width. The trigger is sized to the
 *  widest option, but <input> compares integer clientWidth (floored) against an
 *  integer scrollWidth (ceiled text) — a sub-pixel measurement would trip
 *  text-overflow:ellipsis on the very widest option. Ceil + buffer guarantees it
 *  always fits, so the ellipsis only ever appears under a genuine max-width. */
const VALUE_WIDTH_BUFFER_PX = 2;

/** Borderless-only CSS custom properties: inline-label width + frozen value width. */
const buildBorderlessStyle = (
  hasInlineLeft: boolean,
  leftSectionWidth: number,
  valueWidth: number
): React.CSSProperties => {
  const style: Record<string, string> = {};
  if (hasInlineLeft) style['--borderless-left-width'] = `${leftSectionWidth}px`;
  if (valueWidth > 0) {
    const frozen = Math.ceil(valueWidth) + VALUE_WIDTH_BUFFER_PX;
    style['--borderless-value-width'] = `${frozen}px`;
  }
  return style as React.CSSProperties;
};

/**
 * Sequential value fade for the borderless variant: on change, the displayed
 * value lags one step while the old text fades out, then swaps to the new value
 * and fades in. Returns the value to render and the `data-fade` phase. When
 * disabled, tracks `value` instantly (no lag, no fade phase).
 */
function useSequentialFade(
  value: string | null | undefined,
  enabled: boolean
): {
  displayValue: string | null | undefined;
  fadeState: 'out' | 'in' | undefined;
} {
  const [displayValue, setDisplayValue] = useState(value);
  const [fading, setFading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) {
      setDisplayValue(value);
      setFading(false);
      return undefined;
    }
    if (value === displayValue) return undefined;
    setFading(true);
    timerRef.current = setTimeout(() => {
      setDisplayValue(value);
      setFading(false);
    }, FADE_DURATION_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, enabled, displayValue]);

  // When disabled, mirror `value` synchronously (no one-frame lag / flicker).
  if (!enabled) return { displayValue: value, fadeState: undefined };
  return { displayValue, fadeState: fading ? 'out' : 'in' };
}

export const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      size = 'md',
      label,
      error,
      disabled,
      isReadOnly,
      placeholder,
      isMasked = false,
      clearable,
      onClear,
      optionPrefix,
      isTransparent = false,
      variant,
      shouldRightAlignValue = false,
      readOnlyVariant = 'label',
      renderOption: renderOptionProp,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref
  ) => {
    // Track value for both controlled and uncontrolled usage.
    // Needed to toggle between clear button and chevron in rightSection —
    // without this, uncontrolled components always show the combined section
    // (both X and chevron) because the `value` prop is undefined.
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState<
      string | null | undefined
    >(defaultValue);
    const currentValue = isControlled ? value : uncontrolledValue;

    const handleChange: MantineSelectProps['onChange'] = (val, option) => {
      if (!isControlled) {
        setUncontrolledValue(val);
      }
      onChange?.(val, option);
    };

    const renderOption = renderOptionProp ?? createRenderOption(optionPrefix);

    const showClearButton = !!(
      clearable &&
      currentValue &&
      !disabled &&
      !isReadOnly
    );

    const handleClear = (event: React.MouseEvent) => {
      event.stopPropagation();
      handleChange(null, { value: '', label: '' });
      onClear?.();
    };

    const isLockVariant = !!isReadOnly && readOnlyVariant === 'lock';
    const isBorderless = variant === 'borderless';

    const rightSection = renderRightSection(
      showClearButton,
      isLockVariant,
      handleClear
    );

    // Extract consumer's comboboxProps so we can merge classNames rather than
    // letting the spread overwrite our dropdown/options/option class names entirely.
    // Also extract rightSection so that undefined doesn't override our computed section —
    // consumers pass undefined to mean "use default behaviour" (chevron / clear button).
    // Explicit null still suppresses the section entirely.
    const {
      comboboxProps: consumerComboboxProps,
      rightSection: consumerRightSection,
      leftSection: consumerLeftSection,
      ...restProps
    } = props;

    const effectiveRightSection =
      consumerRightSection !== undefined ? consumerRightSection : rightSection;

    // For the borderless (inline) variant the label renders inline, as a
    // secondary-color prefix in front of the value, composed with the
    // consumer's leftSection icon (e.g. a sort/sparkle icon). The stacked
    // Mantine label is suppressed in this mode.
    const hasInlineLeft = computeHasInlineLeft(
      isBorderless,
      label,
      consumerLeftSection
    );

    // The borderless inline label/icon overlay the left of the input (with
    // pointer-events:none, so clicks fall through to the input — the dropdown
    // trigger). Because the input is the full-width trigger, its left padding
    // must clear the label+icon, whose width is dynamic — so we measure it and
    // expose it to CSS as --borderless-left-width.
    const leftSectionRef = useRef<HTMLSpanElement>(null);
    const leftSectionWidth = useMeasuredWidth(leftSectionRef, hasInlineLeft);

    // Freeze the borderless trigger to the widest option so the chevron stops
    // sliding when the value changes: measure a hidden sizer of all labels and
    // expose it to CSS as --borderless-value-width.
    const sizerRef = useRef<HTMLSpanElement>(null);
    const valueWidth = useMeasuredWidth(sizerRef, isBorderless);

    // Sequential value fade (borderless only): render a lagged value so the old
    // text fades out before the new one fades in (see Select.module.css).
    const { displayValue, fadeState } = useSequentialFade(
      currentValue,
      isBorderless
    );

    const effectiveLeftSection = hasInlineLeft ? (
      <BorderlessLeftSection
        ref={leftSectionRef}
        label={label}
        icon={consumerLeftSection}
      />
    ) : (
      consumerLeftSection
    );

    return (
      <>
        <MantineSelect
          ref={ref}
          unstyled
          classNames={{
            root: styles.root,
            wrapper: styles.wrapper,
            input: clsx(styles.input, isMasked && 'fs-mask'),
            section: styles.section,
            label: styles.label,
            error: styles.error,
            required: styles.required,
            description: styles.description,
          }}
          comboboxProps={{
            // Anchor the borderless dropdown to the full trigger width.
            ...(isBorderless && { width: 'target' }),
            ...consumerComboboxProps,
            classNames: {
              dropdown: styles.dropdown,
              options: styles.options,
              option: styles.option,
              ...consumerComboboxProps?.classNames,
            },
          }}
          wrapperProps={{
            'data-input-size': size,
            'data-variant': variant,
            ...(isReadOnly && { 'data-readonly': true }),
            ...(isLockVariant && { 'data-readonly-variant': 'lock' }),
            ...(isTransparent && { 'data-transparent': true }),
            ...(shouldRightAlignValue && { 'data-right-align-value': true }),
            ...(isBorderless && {
              'data-fade': fadeState,
              style: buildBorderlessStyle(
                hasInlineLeft,
                leftSectionWidth,
                valueWidth
              ),
            }),
          }}
          rightSection={effectiveRightSection}
          leftSection={effectiveLeftSection}
          withCheckIcon={false}
          value={displayValue ?? null}
          onChange={handleChange}
          label={isBorderless ? undefined : label}
          error={renderErrorMessage(error, isReadOnly)}
          disabled={disabled}
          readOnly={isReadOnly}
          placeholder={isReadOnly && !isLockVariant ? '-' : placeholder}
          renderOption={renderOption}
          {...restProps}
          __defaultRightSection={null}
        />
        {isBorderless && (
          <ValueSizer
            ref={sizerRef}
            labels={extractOptionLabels(restProps.data)}
            size={size}
          />
        )}
      </>
    );
  }
);

Select.displayName = 'Select';
